import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);
  const [missionRef, missionVisible] = useScrollAnimation(0.3);
  const [visionRef, visionVisible] = useScrollAnimation(0.3);
  const supportingLeaders = [
    {
      name: "Keyur Shah",
      image: "/images/founder_1.png",
      bio: "Keyur Shah brings execution discipline and project coordination strength to the leadership team. He works closely on delivery planning, client alignment, and on-site quality to ensure projects move smoothly from concept to commissioning."
    },
    {
      name: "Hiren Shah",
      image: "/images/founder_2.png",
      bio: "Hiren Shah contributes operational insight with a clear emphasis on consistency, service standards, and sustainable business growth. His leadership supports strong relationships, steady expansion, and a reliable experience across every engagement."
    }
  ];
  const certifications = [
    {
      title: "Crestron Certified Partner",
      mark: "CRESTRON"
    },
    {
      title: "CP PLUS – Mission Securing Bharat Initiative",
      mark: "CP PLUS"
    },
    {
      title: "Stellar Project Recognition – Cavitak",
      mark: "CAVITAK"
    },
    {
      title: "Authorized Dealer – ABC Power Systems",
      mark: "ABC POWER"
    },
    {
      title: "Authorized Dealership – SmartNode",
      mark: "SMARTNODE"
    },
    {
      title: "Startup India Recognition – DPIIT",
      mark: "STARTUP INDIA"
    },
    {
      title: "Udyam Registration – MSME",
      mark: "UDYAM"
    }
  ];

  return (
    <section id="about" className="relative py-24 bg-gradient-to-b from-black via-slate-950 to-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-20">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-20 scroll-fade-in ${headerVisible ? 'visible' : ''}`}
        >
          <h2 className="display-large text-white mb-6">About Us</h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-8"></div>
          <p className="body-large text-white/85 max-w-4xl mx-auto leading-relaxed">
            Smart Automation and LV System is a leading provider of intelligent automation and low voltage solutions.
            With over 15 years of excellence, we transform spaces into smart, efficient, and secure environments through
            cutting-edge technology and expert engineering.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-12">
          <div
            ref={missionRef}
            className={`bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-10 hover:border-blue-500/50 transition-all duration-500 group scroll-slide-left ${missionVisible ? 'visible' : ''}`}
          >
            <h3 className="heading-2 text-white mb-4 group-hover:text-blue-400 transition-colors">Our Mission</h3>
            <p className="body-medium text-white/75">
              To deliver innovative, reliable, and scalable automation solutions that enhance the quality of life
              and operational efficiency for our clients across residential, commercial, and industrial sectors.
            </p>
          </div>
          <div
            ref={visionRef}
            className={`bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-10 hover:border-blue-500/50 transition-all duration-500 group scroll-slide-right ${visionVisible ? 'visible' : ''}`}
          >
            <h3 className="heading-2 text-white mb-4 group-hover:text-blue-400 transition-colors">Our Vision</h3>
            <p className="body-medium text-white/75">
              To be the most trusted name in smart automation and low voltage systems, setting industry standards
              through innovation, quality, and customer satisfaction in every project we undertake.
            </p>
          </div>
        </div>

        {/* Leadership */}
        <div className="mt-24 py-24">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent">
              Leadership
            </h3>
            <div className="w-24 h-1 bg-blue-500/70 mx-auto mt-5"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="flex flex-col items-center lg:items-start">
              <div className="flex items-center justify-center lg:justify-start">
                <img
                  src="/images/founder1.png"
                  alt="Pranav Shah"
                  className="w-full max-w-sm rounded-full border border-white/10 shadow-lg shadow-blue-500/10 hover:scale-[1.02] transition-all duration-300"
                  loading="lazy"
                />
              </div>

              <div className="mt-8 text-center lg:text-left">
                <h4 className="text-3xl font-semibold text-white">Pranav Shah</h4>
                <p className="text-blue-400 tracking-wide mt-2">Founder &amp; Managing Director</p>
                <div className="w-16 h-[2px] bg-blue-500/60 mt-4 mb-6 mx-auto lg:mx-0"></div>

                <div className="space-y-2">
                  <p className="text-gray-300">
                    <span className="text-white font-semibold">17+ </span>
                    Years Experience
                  </p>
                  <p className="text-gray-300">
                    <span className="text-white font-semibold">1500+ </span>
                    Projects Delivered
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-gray-400 leading-relaxed max-w-xl">
                With over 15 years of leadership in smart automation and low voltage delivery, Pranav Shah directs
                engineering-focused execution across residential, commercial, and industrial environments. His approach
                emphasizes reliability, integration clarity, and long-term infrastructure performance at enterprise scale.
              </p>

              <h5 className="text-xl font-semibold text-white mt-10 mb-5">Key Expertise</h5>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
                <li className="flex items-start gap-3 text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                  Intelligent Building Automation
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                  Audio Visual System Integration
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                  Security &amp; Surveillance Integration
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                  Low Voltage System Execution
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                  Wifi &amp; wired network solutions
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {supportingLeaders.map((leader) => (
              <div
                key={leader.name}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-7 transition-all duration-300 hover:border-blue-500/30"
              >
                <div className="flex flex-col items-center text-center">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="h-52 w-52 rounded-full object-cover border border-white/10 shadow-lg shadow-blue-500/10"
                    loading="lazy"
                  />
                  <h4 className="mt-6 text-2xl font-semibold text-white">{leader.name}</h4>
                  <p className="mt-2 text-sm uppercase tracking-[0.24em] text-blue-400">
                    Managing Director
                  </p>
                  <div className="mt-4 h-px w-16 bg-blue-500/60"></div>
                  <p className="mt-6 text-sm leading-7 text-gray-400">
                    {leader.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications & Authorizations */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-semibold text-white mb-3">
              Certifications &amp; Authorizations
            </h3>
            <p className="text-gray-400 text-base max-w-2xl mx-auto">
              Recognized standards and authorizations that reinforce delivery confidence.
            </p>
          </div>

          <div className="flex flex-wrap items-start justify-center gap-x-10 gap-y-10 max-w-5xl mx-auto">
            {certifications.map((item, index) => (
              <div
                key={index}
                className="w-[140px] text-center grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              >
                <div className="h-10 flex items-center justify-center">
                  <span className="text-white/90 text-sm md:text-base font-semibold tracking-[0.16em]">
                    {item.mark}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mt-2 leading-snug">
                  {item.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
